import { useId } from 'react';
import styles from './Select.module.css';

export type SelectProps<T extends string> = {
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
  label?: string;
  /** Keeps the label as the accessible name but does not show it (e.g. a form grid shows it). */
  hideLabel?: boolean;
  disabled?: boolean;
};

export function Select<T extends string>({
  value,
  options,
  onChange,
  label,
  hideLabel = false,
  disabled = false,
}: SelectProps<T>) {
  const id = useId();
  return (
    <div className={styles['field']}>
      {label !== undefined && (
        <label className={hideLabel ? styles['hiddenLabel'] : styles['label']} htmlFor={id}>
          {label}
        </label>
      )}
      <div className={styles['control']}>
        <select
          id={id}
          className={styles['select']}
          value={value}
          disabled={disabled}
          onChange={(event) => {
            const chosen = options.find((option) => option.value === event.target.value);
            if (chosen !== undefined) onChange(chosen.value);
          }}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {/* The platform arrow is turned off in CSS: WebKitGTK draws it in the GTK theme. */}
        <svg
          className={styles['caret']}
          width="14"
          height="14"
          viewBox="0 0 14 14"
          aria-hidden="true"
          focusable="false"
        >
          <path
            d="M3 5.25 7 9.25l4-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    </div>
  );
}
